/**
 * Rehband – Knee Sleeve Finder 2.0
 * Behaviour for sections/knee-sleeve-finder-v2.liquid: sport -> support level -> recommendation.
 *
 * - The current selection lives in the URL (?ksf_sport=…&ksf_level=…), so a result survives reloads, the back
 *   button and can be shared or linked to from other pages.
 * - The last result is remembered for 30 days (localStorage) when the visitor allows preference cookies through the
 *   Shopify Customer Privacy API; without that consent it is only kept for the current tab (sessionStorage).
 * - Results are ranked from the product data rendered by Liquid (support level + sport metaobject handles).
 * - Display "drawer": a compact teaser opens the finder in a native <dialog> (bottom sheet on phones, side panel on
 *   desktop). The URL only carries the selection while the drawer is open; deep links open it automatically.
 * - Emits the DOM events "knee-sleeve-finder:result" / "knee-sleeve-finder:click", pushes the same data to the GTM
 *   dataLayer (if present) and publishes Shopify custom pixel events.
 */

const STEPS = ['sport', 'level', 'result'];
const PARAM_SPORT = 'ksf_sport';
const PARAM_LEVEL = 'ksf_level';
const STORAGE_KEY = 'rehband:knee-sleeve-finder';
const STORAGE_TTL = 30 * 24 * 60 * 60 * 1000;
const AUTO_ADVANCE_DELAY = 280;

const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

// Last input on the page. A question heading gets focus on every step (for screen readers), but its focus ring only
// helps keyboard users – after a click, a tap or a page load (deep link, mega menu) the browser would still draw it.
let lastInput = 'none';
document.addEventListener('keydown', () => (lastInput = 'keyboard'), true);
document.addEventListener('pointerdown', () => (lastInput = 'pointer'), true);

const fill = (template = '', values = {}) =>
  template.replace(/%(\w+)%/g, (placeholder, key) => (key in values ? values[key] : placeholder));

function decodeEntities(value) {
  if (typeof value !== 'string' || !value.includes('&')) return value;
  const textarea = document.createElement('textarea');
  textarea.innerHTML = value;
  return textarea.value;
}

function getStorage(type) {
  try {
    const storage = window[type];
    const probe = '__ksf__';
    storage.setItem(probe, probe);
    storage.removeItem(probe);
    return storage;
  } catch (error) {
    return null;
  }
}

/** Resolves with Shopify's Customer Privacy API (loaded on demand) or null when it is not available. */
function loadCustomerPrivacy() {
  return new Promise((resolve) => {
    const shopify = window.Shopify;
    if (shopify && shopify.customerPrivacy) return resolve(shopify.customerPrivacy);
    if (!shopify || typeof shopify.loadFeatures !== 'function') return resolve(null);

    const timeout = setTimeout(() => resolve(null), 3000);
    try {
      shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (error) => {
        clearTimeout(timeout);
        resolve(error ? null : window.Shopify.customerPrivacy || null);
      });
    } catch (error) {
      clearTimeout(timeout);
      resolve(null);
    }
  });
}

function preferencesAllowed() {
  try {
    const privacy = window.Shopify && window.Shopify.customerPrivacy;
    return Boolean(privacy && typeof privacy.preferencesProcessingAllowed === 'function' && privacy.preferencesProcessingAllowed());
  } catch (error) {
    return false;
  }
}

function publish(eventName, detail) {
  try {
    if (Array.isArray(window.dataLayer)) window.dataLayer.push({ event: eventName, knee_sleeve_finder: detail });
  } catch (error) {
    /* analytics must never break the finder */
  }
  try {
    if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
      window.Shopify.analytics.publish(eventName, detail);
    }
  } catch (error) {
    /* analytics must never break the finder */
  }
}

class KneeSleeveFinder extends HTMLElement {
  connectedCallback() {
    if (this.config) return;

    const configElement = this.querySelector('[data-ksf-config]');
    if (!configElement) return;

    try {
      this.config = JSON.parse(configElement.textContent);
    } catch (error) {
      console.warn('[knee-sleeve-finder] Invalid configuration', error);
      return;
    }

    this.strings = Object.fromEntries(
      Object.entries(this.config.strings || {}).map(([key, value]) => [key, decodeEntities(value)])
    );
    this.levels = (this.config.levels || []).map((level) => ({ ...level, label: decodeEntities(level.label) }));
    this.sports = this.config.sports || {};
    const levelHandles = this.levels.map((level) => level.handle);
    Object.values(this.sports).forEach((sport) => {
      sport.label = decodeEntities(sport.label);
      // Support levels offered for this sport (all, if the merchant switched every level off by mistake)
      const offered = (sport.levels || []).filter((handle) => levelHandles.includes(handle));
      sport.levels = offered.length ? offered : levelHandles;
      sport.paths = sport.paths || {};
    });
    this.sportNames = this.config.sportNames || {};

    this.form = this.querySelector('[data-ksf-form]');
    this.stepElements = Object.fromEntries(STEPS.map((step) => [step, this.querySelector(`[data-ksf-step="${step}"]`)]));
    this.cards = Array.from(this.querySelectorAll('[data-ksf-card]'));
    this.pool = this.querySelector('[data-ksf-pool]');
    this.liveRegion = this.querySelector('[data-ksf-live]');
    this.dialog = this.dataset.mode === 'drawer' ? this.querySelector('[data-ksf-dialog]') : null;
    this.scroller = this.querySelector('[data-ksf-scroll]');
    this.state = { sport: null, level: null, step: 'sport' };
    this.usingPointer = false;

    this.onConsentChange = this.onConsentChange.bind(this);
    this.onDocumentClick = this.onDocumentClick.bind(this);
    this.onHashChange = this.onHashChange.bind(this);

    this.addEventListener('pointerdown', () => {
      this.usingPointer = true;
    });
    this.addEventListener('keydown', () => {
      this.usingPointer = false;
    });
    this.addEventListener('click', (event) => this.onClick(event));
    this.addEventListener('shopify:block:select', (event) => {
      // Theme editor: selecting a sport block shows step 1 so the tile is visible.
      if (!event.target.closest || !event.target.closest('.ksf-option')) return;
      if (this.dialog) this.openDrawer({ focus: false });
      this.goTo('sport', { focus: false });
    });
    if (this.dialog) {
      this.dialog.addEventListener('close', () => this.onDrawerClose());
      this.dialog.addEventListener('click', (event) => {
        // A click on the backdrop targets the dialog itself, outside of its box.
        if (event.target !== this.dialog) return;
        const box = this.dialog.getBoundingClientRect();
        const inside =
          event.clientX >= box.left && event.clientX <= box.right && event.clientY >= box.top && event.clientY <= box.bottom;
        if (!inside) this.dialog.close();
      });
      // Links to "#<finder id>" on this page (e.g. the mega menu image) open the drawer without a reload.
      document.addEventListener('click', this.onDocumentClick);
      window.addEventListener('hashchange', this.onHashChange);
    }
    this.form?.addEventListener('change', (event) => this.onChange(event));
    this.form?.addEventListener('keydown', (event) => {
      // Enter on a radio does not submit a form in every browser – confirm the step explicitly.
      if (event.key === 'Enter' && event.target.matches('.ksf-option__input')) {
        event.preventDefault();
        this.next();
      }
    });
    this.form?.addEventListener('submit', (event) => {
      event.preventDefault();
      this.next();
    });
    document.addEventListener('visitorConsentCollected', this.onConsentChange);

    this.restore();
  }

  disconnectedCallback() {
    document.removeEventListener('visitorConsentCollected', this.onConsentChange);
    document.removeEventListener('click', this.onDocumentClick);
    window.removeEventListener('hashchange', this.onHashChange);
    document.documentElement.classList.remove('ksf-scroll-lock');
    clearTimeout(this.advanceTimer);
    clearTimeout(this.announceTimer);
  }

  /* ------------------------------------------------------------------------------------------------------------------
     Events
     ---------------------------------------------------------------------------------------------------------------- */

  onChange(event) {
    const { target } = event;

    if (target.matches('[data-ksf-sport-select]')) {
      this.setSport(target.value || null);
    } else if (target.name === 'ksf-sport' && target.checked) {
      this.setSport(target.value);
    } else if (target.name === 'ksf-level' && target.checked) {
      this.setLevel(target.value);
    }
  }

  onClick(event) {
    const { target } = event;

    // Auto-advance only for mouse/touch: arrow keys also "click" radios and must not move the visitor on.
    if (target.matches('.ksf-option__input') && this.usingPointer && this.config.autoAdvance) {
      clearTimeout(this.advanceTimer);
      this.advanceTimer = setTimeout(() => this.next(), prefersReducedMotion() ? 0 : AUTO_ADVANCE_DELAY);
      return;
    }

    const actionElement = target.closest('[data-ksf-action]');
    if (actionElement && this.contains(actionElement)) {
      this.runAction(actionElement.dataset.ksfAction, actionElement);
      return;
    }

    const trackedElement = target.closest('[data-ksf-track]');
    if (trackedElement) this.trackClick(trackedElement);
  }

  runAction(action, element) {
    const sport = this.currentSport();

    switch (action) {
      case 'open':
        this.openDrawer({ source: 'teaser' });
        break;

      case 'close':
        if (this.dialog && this.dialog.open) this.dialog.close();
        break;

      case 'back':
      case 'edit-sport':
        this.goTo('sport');
        break;

      case 'edit-level':
        this.goTo('level');
        break;

      case 'use-recommended':
      case 'apply-recommended':
        if (sport && this.levelOffered(sport.recommended)) {
          this.setLevel(sport.recommended);
          this.showResult();
        }
        break;

      case 'resume':
        if (this.resumeState) {
          if (this.dialog) {
            this.openDrawer({ source: 'resume', focus: false });
          } else {
            this.hideResume();
          }
          this.setSport(this.resumeState.sport);
          this.setLevel(this.resumeState.level);
          this.showResult();
        }
        break;

      case 'resume-dismiss':
        this.hideResume();
        this.clearStored();
        this.focusHeading('sport');
        break;

      case 'restart':
        this.restart();
        break;

      default:
        break;
    }
  }

  onConsentChange() {
    // Consent may be granted after the result was shown: remember it now.
    if (this.state.step === 'result') this.persist();
  }

  /* ------------------------------------------------------------------------------------------------------------------
     State
     ---------------------------------------------------------------------------------------------------------------- */

  currentSport() {
    return (this.state.sport && this.sports[this.state.sport]) || null;
  }

  levelIndex(handle) {
    return this.levels.findIndex((level) => level.handle === handle);
  }

  hasLevel(handle) {
    return this.levelIndex(handle) >= 0;
  }

  /** Is the support level offered for the given sport (default: the selected one)? */
  levelOffered(handle, sport = this.currentSport()) {
    return this.hasLevel(handle) && (!sport || sport.levels.includes(handle));
  }

  levelLabel(handle) {
    const level = this.levels[this.levelIndex(handle)];
    return level ? level.label : '';
  }

  sportName(handle) {
    // Prefer the finder's own (translated) tile label, then the metaobject name.
    const sport = Object.values(this.sports).find((entry) => (entry.match || []).includes(handle));
    if (sport) return sport.label;
    return this.sportNames[handle] ? decodeEntities(this.sportNames[handle]) : handle;
  }

  setSport(key) {
    const sport = key && this.sports[key] ? key : null;
    this.state.sport = sport;

    this.querySelectorAll('input[name="ksf-sport"]').forEach((input) => {
      input.checked = input.value === sport;
    });

    const select = this.querySelector('[data-ksf-sport-select]');
    if (select) {
      const inSelect = Boolean(sport) && Array.from(select.options).some((option) => option.value === sport);
      select.value = inSelect ? sport : '';
      select.classList.toggle('is-filled', inSelect);
    }

    this.hideError('sport');
    this.updateLevelStep();
  }

  setLevel(handle) {
    const level = this.levelOffered(handle) ? handle : null;
    this.state.level = level;

    this.querySelectorAll('input[name="ksf-level"]').forEach((input) => {
      input.checked = input.value === level;
    });

    this.hideError('level');
  }

  next() {
    clearTimeout(this.advanceTimer);

    if (this.state.step === 'sport') {
      if (!this.state.sport) return this.showError('sport');
      this.goTo('level');
    } else if (this.state.step === 'level') {
      if (!this.state.level) return this.showError('level');
      this.showResult();
    }

    return undefined;
  }

  restart() {
    this.setSport(null);
    this.setLevel(null);
    this.hideResume();
    this.clearStored();
    this.cards.forEach((card) => {
      card.hidden = true;
      this.pool.append(card);
    });
    this.goTo('sport');
  }

  /* ------------------------------------------------------------------------------------------------------------------
     Steps, focus and announcements
     ---------------------------------------------------------------------------------------------------------------- */

  goTo(step, { focus = true } = {}) {
    if (!STEPS.includes(step)) return;

    clearTimeout(this.advanceTimer);
    this.state.step = step;
    this.dataset.step = step;

    STEPS.forEach((name) => {
      if (this.stepElements[name]) this.stepElements[name].hidden = name !== step;
    });

    this.updateStepper();
    if (step === 'level') this.updateLevelStep();
    this.syncUrl();

    if (focus) this.focusHeading(step);
  }

  focusHeading(step) {
    const heading = this.stepElements[step]?.querySelector('[data-ksf-heading]');
    if (!heading) return;

    if (lastInput !== 'keyboard') {
      heading.classList.add('is-quiet-focus');
      heading.addEventListener('blur', () => heading.classList.remove('is-quiet-focus'), { once: true });
    }
    heading.focus({ preventScroll: true });

    // In the drawer, every step starts at the top of the panel.
    if (this.dialog && this.scroller) {
      this.scroller.scrollTo({ top: 0, behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
      return;
    }

    // Keep the progress + question in view on small screens (the theme adds the sticky header offset).
    const anchor = this.querySelector('[data-ksf-top]') || this;
    const { top } = anchor.getBoundingClientRect();
    if (top < 0 || top > window.innerHeight * 0.6) {
      anchor.scrollIntoView({ block: 'start', behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
    }
  }

  /* ------------------------------------------------------------------------------------------------------------------
     Drawer (display "drawer")
     ---------------------------------------------------------------------------------------------------------------- */

  openDrawer({ source = 'teaser', focus = true } = {}) {
    if (!this.dialog) return;

    if (!this.dialog.open) {
      this.opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      this.dialog.showModal();
      document.documentElement.classList.add('ksf-scroll-lock');
      publish('knee_sleeve_finder_open', { source, sport: this.state.sport });
    }

    this.goTo(this.state.step, { focus });
  }

  onDrawerClose() {
    clearTimeout(this.advanceTimer);
    document.documentElement.classList.remove('ksf-scroll-lock');
    this.syncUrl();

    // The teaser offers the last result again.
    if (this.state.step === 'result' && this.state.sport && this.state.level) {
      this.showResume({ sport: this.state.sport, level: this.state.level });
    }

    if (this.opener && this.opener.isConnected) this.opener.focus({ preventScroll: true });
    this.opener = null;
  }

  // A link to "#<finder id>" on the same page (the mega menu image on the collection itself) does not reload the
  // page – open the drawer directly. From other pages the browser loads the collection and restore() opens it.
  onDocumentClick(event) {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const link = event.target instanceof Element ? event.target.closest('a[href]') : null;
    if (!link || !this.id || (link.target && link.target !== '_self')) return;

    let url;
    try {
      url = new URL(link.href, window.location.href);
    } catch (error) {
      return;
    }
    if (url.hash !== `#${this.id}` || url.origin !== window.location.origin) return;
    if (url.pathname.replace(/\/$/, '') !== window.location.pathname.replace(/\/$/, '')) return;

    event.preventDefault();
    this.closeThemeMenu(link);
    this.openDrawer({ source: 'link' });
  }

  onHashChange() {
    if (this.id && window.location.hash === `#${this.id}`) this.openDrawer({ source: 'link' });
  }

  // The theme's mobile navigation drawer or mega menu should not stay open behind the finder.
  closeThemeMenu(link) {
    try {
      const drawer = link.closest('navigation-drawer');
      if (drawer && typeof drawer.hide === 'function') drawer.hide();
      const menu = link.closest('details[open]');
      if (menu) menu.open = false;
    } catch (error) {
      /* ignore – the theme keeps its menu open */
    }
  }

  updateStepper() {
    const current = STEPS.indexOf(this.state.step);

    this.querySelectorAll('[data-ksf-stepper-item]').forEach((item, index) => {
      item.classList.toggle('is-current', index === current);
      item.classList.toggle('is-complete', index < current);

      if (index === current) {
        item.setAttribute('aria-current', 'step');
      } else {
        item.removeAttribute('aria-current');
      }

      const state = item.querySelector('[data-ksf-stepper-state]');
      if (state) state.textContent = index < current ? ` (${this.strings.completed})` : '';
    });
  }

  updateLevelStep() {
    const sport = this.currentSport();
    const recommended = sport && this.levelOffered(sport.recommended) ? sport.recommended : null;

    // Only the support levels that make sense for the sport are offered (e.g. no 7 mm sleeve for running)
    if (this.state.level && !this.levelOffered(this.state.level)) this.setLevel(null);
    const grid = this.querySelector('.ksf-levels');
    if (grid) grid.style.setProperty('--ksf-level-count', String(sport ? sport.levels.length : this.levels.length));

    this.querySelectorAll('[data-ksf-level-option]').forEach((option) => {
      const input = option.querySelector('input');
      const offered = this.levelOffered(option.dataset.level);
      option.hidden = !offered;
      if (input) input.disabled = !offered;
      const badge = option.querySelector('[data-ksf-recommended]');
      const isRecommended = option.dataset.level === recommended;

      if (badge) {
        badge.hidden = !isRecommended;
        badge.textContent = isRecommended ? fill(this.strings.recommendedFor, { sport: sport.label }) : '';
      }

      if (input) {
        const describedBy = [input.dataset.describedby, isRecommended && badge ? badge.id : null].filter(Boolean);
        input.setAttribute('aria-describedby', describedBy.join(' '));
      }
    });

    const useRecommended = this.querySelector('[data-ksf-action="use-recommended"]');
    if (useRecommended) {
      useRecommended.hidden = !recommended;
      useRecommended.textContent = recommended ? fill(this.strings.useRecommended, { sport: sport.label }) : '';
    }

    this.querySelectorAll('[data-ksf-context-sport]').forEach((element) => {
      element.textContent = sport ? sport.label : '';
    });
  }

  showError(step) {
    const error = this.querySelector(`[data-ksf-error="${step}"]`);
    const fieldset = this.stepElements[step]?.querySelector('fieldset');
    const message = step === 'sport' ? this.strings.errorSport : this.strings.errorLevel;

    if (error) {
      error.hidden = false;
      error.textContent = '';
      // Re-inserting the text makes screen readers announce the alert every time.
      requestAnimationFrame(() => {
        error.textContent = message;
      });
      fieldset?.setAttribute('aria-describedby', error.id);
    }

    const firstControl = this.stepElements[step]?.querySelector('input[type="radio"], select');
    firstControl?.focus();
  }

  hideError(step) {
    const error = this.querySelector(`[data-ksf-error="${step}"]`);
    if (!error || error.hidden) return;

    error.hidden = true;
    error.textContent = '';
    this.stepElements[step]?.querySelector('fieldset')?.removeAttribute('aria-describedby');
  }

  announce(message) {
    if (!this.liveRegion) return;

    this.liveRegion.textContent = '';
    clearTimeout(this.announceTimer);
    this.announceTimer = setTimeout(() => {
      this.liveRegion.textContent = message;
    }, 150);
  }

  /* ------------------------------------------------------------------------------------------------------------------
     Recommendation
     ---------------------------------------------------------------------------------------------------------------- */

  /**
   * Ranks every product for the selected sport + support level.
   * - Curated path of the sport block (top pick + alternatives per support level) wins.
   * - Otherwise top pick: the closest support level, then the best sport match, then the merchant's product order.
   * - Alternatives: the sleeve made for the sport (if the top pick is not), an equally good fit, one level more and
   *   one level less support, then other sport-relevant sleeves on support levels that are not shown yet.
   */
  rank(sportKey, levelHandle) {
    const sport = this.sports[sportKey];
    const target = this.levelIndex(levelHandle);
    if (!sport || target < 0) return { primary: null, alternatives: [] };

    const match = new Set(sport.match || []);
    const related = new Set(sport.related || []);
    const extraProducts = new Set(sport.products || []);

    const candidates = this.cards
      .map((card, index) => {
        const level = this.levelIndex(card.dataset.level);
        if (level < 0) return null;

        const productSports = (card.dataset.sports || '').split(' ').filter(Boolean);
        let sportScore = 0;
        let via = null;

        if (extraProducts.has(card.dataset.handle) || productSports.some((handle) => match.has(handle))) {
          sportScore = 2;
        } else {
          via = productSports.find((handle) => related.has(handle)) || null;
          if (via) sportScore = 1;
        }

        return {
          card,
          index,
          handle: card.dataset.handle,
          level,
          delta: level - target,
          distance: Math.abs(level - target),
          sportScore,
          via,
        };
      })
      .filter(Boolean);

    const byFit = (a, b) => a.distance - b.distance || b.sportScore - a.sportScore || a.index - b.index;
    const bySport = (a, b) => b.sportScore - a.sportScore || a.distance - b.distance || a.index - b.index;
    const best = (list) => [...list].sort(bySport)[0] || null;

    const sorted = [...candidates].sort(byFit);
    // Curated path (theme editor): top pick first, then the alternatives in the merchant's order
    const curated = (sport.paths[levelHandle] || [])
      .map((handle) => candidates.find((candidate) => candidate.handle === handle))
      .filter((candidate, position, list) => candidate && list.indexOf(candidate) === position);
    const primary = curated[0] || sorted[0] || null;
    if (!primary) return { primary: null, alternatives: [] };

    const others = sorted.filter((candidate) => candidate !== primary);
    const limit = Math.max(0, Number(this.config.alternatives) || 0);
    const alternatives = [];
    const add = (candidate) => {
      if (candidate && alternatives.length < limit && !alternatives.includes(candidate)) alternatives.push(candidate);
    };

    curated.slice(1).forEach(add);
    if (curated.length > 1) return { primary, alternatives };

    if (primary.sportScore < 2) add(best(others.filter((candidate) => candidate.sportScore === 2)));
    add(best(others.filter((candidate) => candidate.delta === 0 && candidate.sportScore > 0)));
    add(best(others.filter((candidate) => candidate.delta === 1)));
    add(best(others.filter((candidate) => candidate.delta === -1)));
    // A curated top pick can sit on another level: then also offer the exact level the visitor asked for.
    if (primary.delta !== 0) add(best(others.filter((candidate) => candidate.delta === 0)));

    // Fill up with relevant sleeves, preferring support levels that are not shown yet (real trade-offs, no twins).
    while (alternatives.length < limit) {
      const shownLevels = new Set([primary, ...alternatives].map((entry) => entry.level));
      const next = others
        .filter((candidate) => !alternatives.includes(candidate) && (candidate.sportScore > 0 || candidate.distance <= 1))
        .sort(
          (a, b) =>
            Number(shownLevels.has(a.level)) - Number(shownLevels.has(b.level)) ||
            b.sportScore - a.sportScore ||
            a.distance - b.distance ||
            a.index - b.index
        )[0];
      if (!next) break;
      alternatives.push(next);
    }

    return { primary, alternatives };
  }

  reasonsFor(entry, sport) {
    const reasons = [];
    const levelLabel = this.levels[entry.level].label;

    if (entry.sportScore === 2) {
      reasons.push({ type: 'match', text: fill(this.strings.reasonSport, { sport: sport.label }) });
    } else if (entry.sportScore === 1) {
      reasons.push({ type: 'related', text: fill(this.strings.reasonRelated, { sport: this.sportName(entry.via) }) });
    }

    if (entry.delta === 0) {
      reasons.push({ type: 'match', text: fill(this.strings.reasonLevelExact, { level: levelLabel }) });
    } else if (entry.delta > 0) {
      reasons.push({ type: 'more', text: fill(this.strings.reasonLevelMore, { level: levelLabel }) });
    } else {
      reasons.push({ type: 'less', text: fill(this.strings.reasonLevelLess, { level: levelLabel }) });
    }

    return reasons;
  }

  /** Badges for the alternatives: "Made for <sport>" once (if the top pick is not), otherwise how they differ. */
  badgesFor(primary, alternatives, sport) {
    let sportBadgeUsed = primary.sportScore === 2;

    return alternatives.map((entry) => {
      if (entry.sportScore === 2 && !sportBadgeUsed) {
        sportBadgeUsed = true;
        return { kind: 'sport', text: fill(this.strings.badgeSport, { sport: sport.label }) };
      }
      if (entry.level > primary.level) return { kind: 'more', text: this.strings.badgeMore };
      if (entry.level < primary.level) return { kind: 'less', text: this.strings.badgeLess };
      return { kind: 'same', text: this.strings.badgeSame };
    });
  }

  icon(type) {
    const template = this.querySelector(`template[data-ksf-icon="${type}"]`);
    const svg = template?.content.firstElementChild;
    if (!svg) return null;

    const wrapper = document.createElement('span');
    wrapper.className = 'ksf-reasons__icon';
    wrapper.setAttribute('aria-hidden', 'true');
    wrapper.append(svg.cloneNode(true));
    return wrapper;
  }

  decorate(entry, primary, sport, badge) {
    const { card } = entry;
    const isPrimary = entry === primary;

    card.hidden = false;
    card.classList.toggle('ksf-card--primary', isPrimary);
    card.classList.toggle('ksf-card--alternative', !isPrimary);
    card.querySelector('[data-ksf-card-title]')?.setAttribute('aria-level', isPrimary ? '4' : '5');

    const badgeElement = card.querySelector('[data-ksf-badge]');
    if (badgeElement) {
      badgeElement.textContent = badge.text;
      badgeElement.dataset.kind = badge.kind;
    }

    const list = card.querySelector('[data-ksf-reasons]');
    if (list) {
      list.replaceChildren(
        ...this.reasonsFor(entry, sport).map(({ type, text }) => {
          const item = document.createElement('li');
          item.dataset.type = type;
          const icon = this.icon(type);
          if (icon) item.append(icon);
          const label = document.createElement('span');
          label.textContent = text;
          item.append(label);
          return item;
        })
      );
    }
  }

  showResult({ focus = true, announce = true, persist = true, track = true } = {}) {
    const sport = this.currentSport();
    if (!sport || !this.levelOffered(this.state.level)) return;

    const { primary, alternatives } = this.rank(this.state.sport, this.state.level);
    const primaryWrapper = this.querySelector('[data-ksf-primary]');
    const alternativesWrapper = this.querySelector('[data-ksf-alternatives]');
    const alternativesSection = this.querySelector('[data-ksf-alternatives-wrapper]');
    const empty = this.querySelector('[data-ksf-empty]');

    this.cards.forEach((card) => {
      card.hidden = true;
      card.classList.remove('ksf-card--primary', 'ksf-card--alternative');
      this.pool.append(card);
    });

    if (primary) {
      this.decorate(primary, primary, sport, { kind: 'top', text: this.strings.badgeTop });
      primaryWrapper?.append(primary.card);

      const badges = this.badgesFor(primary, alternatives, sport);
      alternatives.forEach((alternative, index) => {
        this.decorate(alternative, primary, sport, badges[index]);
        alternativesWrapper?.append(alternative.card);
      });
    }

    if (empty) empty.hidden = Boolean(primary);
    if (alternativesSection) alternativesSection.hidden = alternatives.length === 0;

    this.querySelectorAll('[data-ksf-summary-sport]').forEach((element) => {
      element.textContent = sport.label;
    });
    this.querySelectorAll('[data-ksf-summary-level]').forEach((element) => {
      element.textContent = this.levelLabel(this.state.level);
    });

    this.updateLevelHint(sport);
    this.updateCompareLink();
    this.goTo('result', { focus });

    if (announce) {
      const title = (entry) => entry.card.dataset.title;
      let message = primary ? fill(this.strings.announceResult, { product: title(primary) }) : this.strings.noResult;
      if (alternatives.length) {
        message += ` ${fill(this.strings.announceAlternatives, { products: alternatives.map(title).join(', ') })}`;
      }
      this.announce(message);
    }

    if (persist) this.persist();

    if (track && primary) {
      const detail = {
        sport: this.state.sport,
        level: this.state.level,
        product: primary.handle,
        alternatives: alternatives.map((alternative) => alternative.handle),
      };
      this.dispatchEvent(new CustomEvent('knee-sleeve-finder:result', { bubbles: true, detail }));
      publish('knee_sleeve_finder_result', detail);
    }
  }

  updateLevelHint(sport) {
    const hint = this.querySelector('[data-ksf-level-hint]');
    if (!hint) return;

    const recommended = this.levelOffered(sport.recommended, sport) && sport.recommended !== this.state.level ? sport.recommended : null;
    hint.hidden = !recommended;
    if (!recommended) return;

    const label = this.levelLabel(recommended);
    const text = hint.querySelector('[data-ksf-level-hint-text]');
    const button = hint.querySelector('[data-ksf-action="apply-recommended"]');
    if (text) text.textContent = fill(this.strings.levelHint, { sport: sport.label, level: label });
    if (button) button.textContent = fill(this.strings.levelHintButton, { level: label });
  }

  updateCompareLink() {
    const link = this.querySelector('[data-ksf-compare]');
    if (!link || link.getAttribute('href') !== '#') return;

    // The knee sleeve comparison section (its #compare anchor) wins over the theme's comparison table
    const table =
      document.querySelector('.shopify-section--knee-sleeve-comparison .ksg-compare[id]') ||
      document.querySelector('.shopify-section--comparison-table');
    if (table && table.id) {
      link.setAttribute('href', `#${table.id}`);
      link.hidden = false;
    }
  }

  trackClick(element) {
    const card = element.closest('[data-ksf-card]');
    if (!card) return;

    const detail = {
      sport: this.state.sport,
      level: this.state.level,
      product: card.dataset.handle,
      position: card.classList.contains('ksf-card--primary') ? 'top_pick' : 'alternative',
      element: element.dataset.ksfTrack,
    };
    this.dispatchEvent(new CustomEvent('knee-sleeve-finder:click', { bubbles: true, detail }));
    publish('knee_sleeve_finder_click', detail);
  }

  /* ------------------------------------------------------------------------------------------------------------------
     Persistence (URL + storage)
     ---------------------------------------------------------------------------------------------------------------- */

  syncUrl() {
    if (window.Shopify && window.Shopify.designMode) return;

    let url;
    try {
      url = new URL(window.location.href);
    } catch (error) {
      return;
    }

    const { step } = this.state;
    // In drawer mode the selection only belongs in the URL while the drawer is open.
    const active = !this.dialog || this.dialog.open;
    const sport = active ? this.state.sport : null;
    const level = active ? this.state.level : null;
    if (sport && step !== 'sport') url.searchParams.set(PARAM_SPORT, sport);
    else url.searchParams.delete(PARAM_SPORT);
    if (level && step === 'result') url.searchParams.set(PARAM_LEVEL, level);
    else url.searchParams.delete(PARAM_LEVEL);
    // The "#<finder id>" deep link opened the drawer – after closing, a reload should not open it again.
    if (this.dialog && !this.dialog.open && this.id && url.hash === `#${this.id}`) url.hash = '';

    if (url.href !== window.location.href) {
      try {
        window.history.replaceState(window.history.state, '', url.href);
      } catch (error) {
        /* ignore – e.g. sandboxed iframes */
      }
    }
  }

  restore() {
    if (window.Shopify && window.Shopify.designMode) {
      this.previewInEditor();
      return;
    }

    const params = new URLSearchParams(window.location.search);
    // Read before goTo(): syncUrl() removes the anchor while the drawer is closed.
    const anchored = Boolean(this.id) && window.location.hash === `#${this.id}`;
    const sport = params.get(PARAM_SPORT);
    const level = params.get(PARAM_LEVEL);

    if (sport && this.sports[sport]) {
      this.setSport(sport);
      if (this.levelOffered(level)) {
        this.setLevel(level);
        this.showResult({ focus: false, announce: false, persist: false, track: false });
      } else {
        this.goTo('level', { focus: false });
      }
      if (this.dialog) this.openDrawer({ source: 'deep_link', focus: false });
      return;
    }

    this.goTo('sport', { focus: false });
    if (this.dialog && anchored) this.openDrawer({ source: 'deep_link' });
    if (!this.config.remember) return;

    loadCustomerPrivacy().then(() => {
      const stored = this.readStored();
      if (stored && this.state.step === 'sport' && !this.state.sport) this.showResume(stored);
    });
  }

  previewInEditor() {
    const sportKeys = Object.keys(this.sports);
    const preview = this.config.editorPreview;
    if (preview === 'teaser' || (preview !== 'sport' && sportKeys.length === 0)) {
      this.goTo('sport', { focus: false });
      return;
    }
    if (this.dialog) this.openDrawer({ source: 'editor', focus: false });
    if (preview === 'sport') {
      this.goTo('sport', { focus: false });
      return;
    }

    this.setSport(sportKeys[0]);
    if (preview === 'level') {
      this.goTo('level', { focus: false });
      return;
    }

    const sport = this.sports[sportKeys[0]];
    const fallback = sport.levels[Math.min(1, sport.levels.length - 1)];
    this.setLevel(this.levelOffered(sport.recommended) ? sport.recommended : fallback);
    this.showResult({ focus: false, announce: false, persist: false, track: false });
  }

  readStored() {
    const privacy = window.Shopify && window.Shopify.customerPrivacy;
    const sources = [getStorage('sessionStorage')];

    if (preferencesAllowed()) {
      sources.push(getStorage('localStorage'));
    } else if (privacy) {
      // Consent was withdrawn: forget what was remembered before.
      try {
        getStorage('localStorage')?.removeItem(STORAGE_KEY);
      } catch (error) {
        /* ignore */
      }
    }

    for (const storage of sources) {
      if (!storage) continue;
      try {
        const data = JSON.parse(storage.getItem(STORAGE_KEY) || 'null');
        if (
          data &&
          this.sports[data.sport] &&
          this.levelOffered(data.level, this.sports[data.sport]) &&
          Date.now() - Number(data.savedAt || 0) < STORAGE_TTL
        ) {
          return data;
        }
      } catch (error) {
        /* ignore malformed entries */
      }
    }

    return null;
  }

  persist() {
    if (!this.config.remember || this.state.step !== 'result') return;
    if (window.Shopify && window.Shopify.designMode) return;

    const payload = JSON.stringify({ sport: this.state.sport, level: this.state.level, savedAt: Date.now() });

    try {
      getStorage('sessionStorage')?.setItem(STORAGE_KEY, payload);
      if (preferencesAllowed()) getStorage('localStorage')?.setItem(STORAGE_KEY, payload);
    } catch (error) {
      /* storage full or blocked */
    }
  }

  clearStored() {
    ['sessionStorage', 'localStorage'].forEach((type) => {
      try {
        getStorage(type)?.removeItem(STORAGE_KEY);
      } catch (error) {
        /* ignore */
      }
    });
  }

  showResume(stored) {
    const banner = this.querySelector('[data-ksf-resume]');
    if (!banner) return;

    this.resumeState = stored;
    const text = banner.querySelector('[data-ksf-resume-text]');
    if (text) {
      text.textContent = fill(this.strings.resumeText, {
        selection: `${this.sports[stored.sport].label} · ${this.levelLabel(stored.level)}`,
      });
    }
    banner.hidden = false;
  }

  hideResume() {
    const banner = this.querySelector('[data-ksf-resume]');
    if (banner) banner.hidden = true;
  }
}

if (!customElements.get('knee-sleeve-finder-v2')) {
  customElements.define('knee-sleeve-finder-v2', KneeSleeveFinder);
}
